import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../contexts/AuthContext';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Grid,
  Chip,
  Alert,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  BarChartOutlined as ChartIcon,
  AutoAwesome as AIIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  Build as BuildIcon,
  Schedule as ScheduleIcon,
  School as SchoolIcon,
  Lightbulb as LightbulbIcon,
  Chat as ChatIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import MarkdownRenderer from '../../../components/Common/MarkdownRenderer';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { RadarChart } from '@mui/x-charts/RadarChart';
import { ScatterChart } from '@mui/x-charts/ScatterChart';
import PageHeader from '../../../components/Common/PageHeader';
import axios from '../../../api/axios';

/** 学习状态分析报告的 7 个关注点（与后端 LearningStateReport 字段一致） */
const ANALYSIS_SECTIONS = [
  { key: 'overall_summary', title: '整体概览', icon: TimelineIcon, color: '#1976d2' },
  { key: 'gaps_and_weaknesses', title: '查漏补缺', icon: BuildIcon, color: '#ed6c02' },
  { key: 'doing_habits', title: '做题习惯', icon: ScheduleIcon, color: '#9c27b0' },
  { key: 'learning_habits', title: '学习习惯', icon: SchoolIcon, color: '#2e7d32' },
  { key: 'knowledge_blind_spots', title: '知识盲区', icon: LightbulbIcon, color: '#d32f2f' },
  { key: 'dialogue_insights', title: '从对话中发现的问题', icon: ChatIcon, color: '#0288d1' },
  { key: 'improvement_suggestions', title: '改进建议', icon: AssignmentIcon, color: '#388e3c' }
];

function NotebookLearningStatePage() {
  const { notebookId } = useParams();
  const navigate = useNavigate();
  const { selectedCourse } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [analysesList, setAnalysesList] = useState([]);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
  const [currentAnalysisContent, setCurrentAnalysisContent] = useState(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = {};
        // 班级维度：有 offeringId 时传 courseOfferingId；否则只传 courseTemplateId，后端按当前学期解析 offering
        if (selectedCourse?.offeringId != null) params.courseOfferingId = selectedCourse.offeringId;
        else if (selectedCourse?.id != null) params.courseTemplateId = selectedCourse.id;
        const res = await axios.get(`/notebook-examples/notebook/${encodeURIComponent(notebookId)}/learning-state`, { params });
        setData(res.data);
      } catch (err) {
        console.error('Error fetching learning state:', err);
        setError(err.response?.data?.error || '加载学习状态失败');
      } finally {
        setLoading(false);
      }
    };
    if (notebookId) fetchData();
  }, [notebookId, selectedCourse?.offeringId, selectedCourse?.id]);

  const fetchAnalysesList = React.useCallback(async () => {
    if (!notebookId) return;
    try {
      const res = await axios.get(`/notebook-examples/notebook/${encodeURIComponent(notebookId)}/learning-state/analyses`);
      setAnalysesList(res.data || []);
    } catch (err) {
      console.error('Error fetching analyses list:', err);
      setAnalysesList([]);
    }
  }, [notebookId]);

  useEffect(() => {
    if (tabValue === 1 && notebookId) fetchAnalysesList();
  }, [tabValue, notebookId, fetchAnalysesList]);

  // 进入 AI 分析 Tab 且有历史记录时，默认选中并加载最新一条
  useEffect(() => {
    if (tabValue !== 1 || !notebookId || analysesList.length === 0) return;
    const latestId = analysesList[0].id;
    if (selectedAnalysisId != null && analysesList.some((a) => a.id === selectedAnalysisId)) return;
    setSelectedAnalysisId(latestId);
    axios
      .get(`/notebook-examples/notebook/${encodeURIComponent(notebookId)}/learning-state/analyses/${latestId}`)
      .then((res) => setCurrentAnalysisContent(res.data.content))
      .catch((err) => {
        console.error('Error fetching latest analysis:', err);
        setAnalyzeError('加载该条分析失败');
      });
  }, [tabValue, notebookId, analysesList, selectedAnalysisId]);

  const handleGenerateAnalysis = async () => {
    if (!notebookId) return;
    setAnalyzeLoading(true);
    setAnalyzeError(null);
    try {
      const res = await axios.post(`/notebook-examples/notebook/${encodeURIComponent(notebookId)}/learning-state/analyze`, {}, { timeout: 2 * 60 * 1000 });
      setCurrentAnalysisContent(res.data.content);
      setSelectedAnalysisId(res.data.id);
      await fetchAnalysesList();
    } catch (err) {
      const msg = err.response?.data?.error || '生成分析失败';
      setAnalyzeError(msg);
    } finally {
      setAnalyzeLoading(false);
    }
  };

  const handleSelectHistory = async (id) => {
    setSelectedAnalysisId(id);
    try {
      const res = await axios.get(`/notebook-examples/notebook/${encodeURIComponent(notebookId)}/learning-state/analyses/${id}`);
      setCurrentAnalysisContent(res.data.content);
    } catch (err) {
      console.error('Error fetching analysis:', err);
      setAnalyzeError('加载该条分析失败');
    }
  };

  const handleBack = () => {
    navigate(`/questions/notebook/${notebookId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <PageHeader title="学习状态分析" onBack={handleBack} />
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || '未获取到数据'}
        </Alert>
      </Container>
    );
  }

  const { notebook, summary, bySection, submissions = [] } = data;
  const totalQuestions = summary.totalQuestions || 0;
  const totalCompleted = summary.totalCompleted || 0;
  const totalAttempted = summary.totalAttempted || 0;
  const completionRate = summary.completionRate || 0;

  // 进度：整体饼图数据
  const attemptedNotPassed = Math.max(0, totalAttempted - totalCompleted);
  const notAttempted = Math.max(0, totalQuestions - totalAttempted);
  const pieData = [
    { id: 0, value: totalCompleted, label: '已完成', color: '#4caf50' },
    { id: 1, value: attemptedNotPassed, label: '已尝试未通过', color: '#ff9800' },
    { id: 2, value: notAttempted, label: '未尝试', color: '#9e9e9e' }
  ].filter((d) => d.value > 0);

  const sectionLabels = bySection.map((s) => (s.sectionTitle.length > 12 ? s.sectionTitle.slice(0, 11) + '…' : s.sectionTitle));

  return (
    <Container maxWidth="lg" sx={{ py: 2, pb: 4, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <PageHeader
        title={`学习状态分析 · ${notebook.title || notebookId}`}
        onBack={handleBack}
      />

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        sx={{ mt: 3, mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<ChartIcon />} iconPosition="start" label="图表概览" id="learning-state-tab-0" aria-controls="learning-state-panel-0" />
        <Tab icon={<AIIcon />} iconPosition="start" label="AI 分析与总结" id="learning-state-tab-1" aria-controls="learning-state-panel-1" />
      </Tabs>

      {/* Tab 1: 图表概览 */}
      <Box role="tabpanel" hidden={tabValue !== 0} id="learning-state-panel-0" aria-labelledby="learning-state-tab-0">
        {tabValue === 0 && (
          <>
            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Chip label={`总题数 ${totalQuestions}`} color="default" variant="outlined" />
              <Chip label={`已尝试 ${totalAttempted}`} color="primary" variant="outlined" />
              <Chip label={`已通过 ${totalCompleted}`} color="success" variant="outlined" />
              <Chip label={`完成率 ${completionRate}%`} color="success" />
            </Box>

            <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
        {/* 1. 进度：整体完成情况 Donut + 按章节完成率柱状 */}
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Card variant="outlined" sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>
                1. 进度
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                学了多少、还剩多少
              </Typography>
              {pieData.length > 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                  <PieChart
                    series={[
                      {
                        data: pieData,
                        innerRadius: 44,
                        outerRadius: 80,
                        arcLabel: (item) => (item.value ? `${item.value}` : ''),
                        arcLabelMinAngle: 20
                      }
                    ]}
                    width={240}
                    height={220}
                    hideLegend={false}
                  />
                </Box>
              ) : (
                <Typography color="text.secondary">暂无题目数据</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 按章节均分柱状图（已做题目的平均分） */}
        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
          <Card variant="outlined" sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Typography variant="h6" gutterBottom>
                各章节均分
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                已做题目的平均分。青=你，粉=班级最高（需已选班级且班内有提交数据）。
              </Typography>
              {bySection.length > 0 ? (
                <Box sx={{ width: '100%', height: 280, flexShrink: 0 }}>
                  <BarChart
                    xAxis={[
                      {
                        scaleType: 'band',
                        data: sectionLabels,
                        id: 'sections',
                        categoryGapRatio: 0.7
                      }
                    ]}
                    series={[
                      {
                        data: bySection.map((s) => (s.avgScore != null ? s.avgScore : null)),
                        label: '你',
                        color: '#4db6ac'
                      },
                      ...(bySection.some((s) => s.classMaxAvgScore != null)
                        ? [
                            {
                              data: bySection.map((s) => (s.classMaxAvgScore != null ? s.classMaxAvgScore : null)),
                              label: '班级最高',
                              color: '#f06292'
                            }
                          ]
                        : [])
                    ]}
                    yAxis={[{ min: 0 }]}
                    height={260}
                    margin={{ top: 20, right: 20, bottom: 60, left: 50 }}
                  />
                </Box>
              ) : (
                <Typography color="text.secondary">暂无章节数据</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 雷达图：各章节通过率 */}
        {bySection.length > 0 && bySection.length <= 12 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  各章节通过率（雷达图）
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  每根轴为一个章节，数值为通过率 0–100%。蓝色=你，粉色=班级最高（需已选班级且班内有提交数据）。
                </Typography>
                {!(selectedCourse?.offeringId ?? selectedCourse?.id) && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    请先在顶部导航或课程页选择班级，此处才会显示「班级最高」通过率对比。
                  </Alert>
                )}
                {(selectedCourse?.offeringId ?? selectedCourse?.id) && !bySection.some((s) => s.classMaxPassRate != null) && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    当前班级暂无其他学生的提交数据，无法显示「班级最高」。
                  </Alert>
                )}
                <Box sx={{ width: '100%', height: 400, display: 'flex', justifyContent: 'center' }}>
                  <RadarChart
                    height={380}
                    series={[
                      {
                        label: '你',
                        data: bySection.map((s) => (s.attempted > 0 ? s.passRate : null)),
                        color: '#4db6ac'
                      },
                      ...(bySection.some((s) => s.classMaxPassRate != null)
                        ? [
                            {
                              label: '班级最高',
                              data: bySection.map((s) => (s.classMaxPassRate != null ? s.classMaxPassRate : null)),
                              color: '#f06292'
                            }
                          ]
                        : [])
                    ]}
                    radar={{
                      metrics: sectionLabels,
                      max: 100
                    }}
                    slotProps={{
                      legend: { hidden: false }
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* 提交时间散点图：横轴=提交时间，纵轴=得分 */}
        {submissions.length > 0 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  提交时间散点图
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  每个点代表一次提交，横轴为提交时间，纵轴为得分；颜色按得分区分（红→橙→黄→绿）
                </Typography>
                <Box sx={{ width: '100%', height: 360 }}>
                  <ScatterChart
                    height={340}
                    series={[
                      {
                        label: '提交',
                        data: submissions.map((s, i) => {
                          const score = Number(s.score);
                          const z = score <= 10 ? score * 10 : score;
                          return {
                            x: new Date(s.submittedAt).getTime(),
                            y: score,
                            z,
                            id: i
                          };
                        }),
                        markerSize: 6
                      }
                    ]}
                    xAxis={[
                      {
                        scaleType: 'time',
                        label: '提交时间',
                        valueFormatter: (v) => (v != null ? new Date(v).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '')
                      }
                    ]}
                    yAxis={[{ label: '得分' }]}
                    zAxis={[
                      {
                        colorMap: {
                          type: 'piecewise',
                          thresholds: [60, 80, 100],
                          colors: ['#f44336', '#ff9800', '#ffeb3b', '#4caf50']
                        }
                      }
                    ]}
                    margin={{ top: 20, right: 20, bottom: 60, left: 50 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* 2. 掌握：各章节通过数/平均分 */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                2. 掌握
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                各章节通过题数、平均分
              </Typography>
              {bySection.length > 0 ? (
                <Box sx={{ width: '100%', height: 300 }}>
                  <BarChart
                    xAxis={[
                      {
                        scaleType: 'band',
                        data: sectionLabels,
                        id: 'sections2',
                        categoryGapRatio: 0.6,
                        barGapRatio: 0.3
                      }
                    ]}
                    series={[
                      { data: bySection.map((s) => s.passed), label: '已通过', color: '#4caf50' },
                      { data: bySection.map((s) => s.total - s.passed), label: '未通过', color: '#9e9e9e' }
                    ]}
                    height={280}
                    margin={{ top: 20, right: 20, bottom: 60, left: 50 }}
                  />
                </Box>
              ) : (
                <Typography color="text.secondary">暂无数据</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 3. 参与与习惯：各章节最后学习时间 */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                3. 参与与习惯
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                各章节最后提交时间（距今天数，0=今天）
              </Typography>
              {bySection.length > 0 ? (
                <Box sx={{ width: '100%', height: 280 }}>
                  <BarChart
                    xAxis={[
                      {
                        scaleType: 'band',
                        data: sectionLabels,
                        id: 'sections3'
                      }
                    ]}
                    series={[
                      {
                        data: bySection.map((s) => {
                          if (!s.lastSubmittedAt) return 0;
                          const days = Math.floor((Date.now() - new Date(s.lastSubmittedAt)) / (24 * 60 * 60 * 1000));
                          return days;
                        }),
                        label: '距今天数',
                        color: '#ff9800'
                      }
                    ]}
                    height={260}
                    margin={{ top: 20, right: 20, bottom: 60, left: 50 }}
                  />
                </Box>
              ) : (
                <Typography color="text.secondary">暂无提交记录</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 4. 求助与复习：各章节对话条数 */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                4. 求助与复习
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                各章节与 AI 对话条数
              </Typography>
              {bySection.length > 0 ? (
                <Box sx={{ width: '100%', height: 280 }}>
                  <BarChart
                    xAxis={[
                      {
                        scaleType: 'band',
                        data: sectionLabels,
                        id: 'sections4'
                      }
                    ]}
                    series={[
                      {
                        data: bySection.map((s) => s.conversationCount || 0),
                        label: '对话条数',
                        color: '#9c27b0'
                      }
                    ]}
                    height={260}
                    margin={{ top: 20, right: 20, bottom: 60, left: 50 }}
                  />
                </Box>
              ) : (
                <Typography color="text.secondary">暂无对话记录</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 按章节明细表格（可选） */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                章节明细
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                      <th style={{ textAlign: 'left', padding: 8 }}>章节</th>
                      <th style={{ padding: 8 }}>总题数</th>
                      <th style={{ padding: 8 }}>已尝试</th>
                      <th style={{ padding: 8 }}>已通过</th>
                      <th style={{ padding: 8 }}>通过率</th>
                      <th style={{ padding: 8 }}>平均分</th>
                      <th style={{ padding: 8 }}>对话条数</th>
                      <th style={{ padding: 8 }}>最后提交</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bySection.map((s) => (
                      <tr key={s.sectionTitle} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <td style={{ padding: 8 }}>{s.sectionTitle}</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>{s.total}</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>{s.attempted}</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>{s.passed}</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>{s.passRate}%</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>{s.avgScore != null ? s.avgScore : '—'}</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>{s.conversationCount || 0}</td>
                        <td style={{ padding: 8, textAlign: 'center' }}>
                          {s.lastSubmittedAt
                            ? new Date(s.lastSubmittedAt).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
          </>
        )}
      </Box>

      {/* Tab 2: AI 对我学习状态的分析和总结 */}
      <Box role="tabpanel" hidden={tabValue !== 1} id="learning-state-panel-1" aria-labelledby="learning-state-tab-1" sx={{ display: tabValue === 1 ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {tabValue === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, mb: 3 }}>
            <Card variant="outlined" sx={{ mb: 2, flexShrink: 0 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  AI 学习状态分析与总结
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  根据你的进度、掌握、参与习惯、求助与复习等维度，以及你与 AI 的对话记录，生成针对本笔记本的学习分析：查漏补缺、做题/学习习惯、知识盲区与改进建议。
                </Typography>
                {totalAttempted < 5 ? (
                  <Alert severity="info">
                    做题太少，暂无分析。至少完成 5 题后再生成学习状态分析。
                  </Alert>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      startIcon={analyzeLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
                      onClick={handleGenerateAnalysis}
                      disabled={analyzeLoading}
                      sx={{ mb: 2 }}
                    >
                      {analyzeLoading ? '生成中…' : '生成总结'}
                    </Button>
                    {analyzeError && (
                      <Alert severity="error" sx={{ mt: 1 }} onClose={() => setAnalyzeError(null)}>
                        {analyzeError}
                      </Alert>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, alignItems: 'stretch' }}>
              <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                <Card variant="outlined" sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}>
                    <Typography variant="subtitle1" gutterBottom>
                      历史分析
                    </Typography>
                    {analysesList.length === 0 ? (
                      <Typography color="text.secondary" variant="body2">
                        暂无分析
                      </Typography>
                    ) : (
                      <List dense disablePadding>
                        {analysesList.map((a) => (
                          <ListItemButton
                            key={a.id}
                            selected={selectedAnalysisId === a.id}
                            onClick={() => handleSelectHistory(a.id)}
                          >
                            <ListItemText
                              primary={new Date(a.createdAt).toLocaleString('zh-CN', { dateStyle: 'short', timeStyle: 'short' })}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
                <Card variant="outlined" sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {currentAnalysisContent ? (
                      (() => {
                        const raw = currentAnalysisContent.raw;
                        const isStructured = typeof currentAnalysisContent === 'object' && !raw && 'overall_summary' in currentAnalysisContent;
                        if (isStructured) {
                          return ANALYSIS_SECTIONS.map(({ key, title, icon: Icon, color }) => {
                            const text = currentAnalysisContent[key];
                            if (!text || !String(text).trim()) return null;
                            return (
                              <Card
                                key={key}
                                variant="outlined"
                                sx={{
                                  borderLeft: `4px solid ${color}`,
                                  borderRadius: 1,
                                  overflow: 'hidden'
                                }}
                              >
                                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Icon sx={{ fontSize: 20, color }} />
                                    <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                                      {title}
                                    </Typography>
                                  </Box>
                                  <MarkdownRenderer content={String(text)} />
                                </CardContent>
                              </Card>
                            );
                          }).filter(Boolean);
                        }
                        return <MarkdownRenderer content={raw != null ? raw : currentAnalysisContent} />;
                      })()
                    ) : (
                      <Typography color="text.secondary">
                        {analysesList.length === 0 ? '暂无分析。完成至少 5 题后点击「生成总结」获取 AI 分析。' : '在左侧选择一条历史分析查看。'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      </Box>

      <Box sx={{ mt: 3, flexShrink: 0 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} variant="outlined">
          返回技能页
        </Button>
      </Box>
    </Container>
  );
}

export default NotebookLearningStatePage;
